import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { useOffline } from './OfflineProvider';

interface OfflineIndicatorProps {
  position?: 'top' | 'bottom';
  variant?: 'minimal' | 'detailed' | 'toast';
  autoHide?: boolean;
  hideDelay?: number;
  className?: string;
}

const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  position = 'top',
  variant = 'minimal',
  autoHide = true,
  hideDelay = 5000,
  className = '',
}) => {
  const {
    isOnline,
    isOffline,
    retryConnection,
    lastOnlineTime,
    hasBeenOffline,
  } = useOffline();

  const [isVisible, setIsVisible] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [justCameOnline, setJustCameOnline] = useState(false);

  // Show/hide logic
  useEffect(() => {
    if (isOffline) {
      setIsVisible(true);
      setJustCameOnline(false);
    } else if (hasBeenOffline && isOnline) {
      setJustCameOnline(true);
      setIsVisible(true);

      if (autoHide) {
        const timer = setTimeout(() => {
          setIsVisible(false);
          setJustCameOnline(false);
        }, hideDelay);

        return () => clearTimeout(timer);
      }
    }
  }, [isOnline, isOffline, hasBeenOffline, autoHide, hideDelay]);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await retryConnection();
    } finally {
      setIsRetrying(false);
    }
  };

  const getTimeAgo = (date: Date | null): string => {
    if (!date) return '';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 1) return 'baru saja';
    if (diffMins < 60) return `${diffMins}m yang lalu`;
    if (diffHours < 24) return `${diffHours}j yang lalu`;
    return 'lebih dari 1 hari';
  };

  if (!isVisible) return null;

  const positionClasses = {
    top: 'top-0 left-0 right-0',
    bottom: 'bottom-0 left-0 right-0',
  };

  const baseClasses = `
    fixed z-50 transition-all duration-300 ease-in-out
    ${positionClasses[position]}
    ${className}
  `;

  // Minimal variant - just a small status bar
  if (variant === 'minimal') {
    return (
      <div className={`${baseClasses} px-4 py-2`}>
        <div
          className={`
            mx-auto max-w-4xl rounded-lg px-4 py-2 text-sm font-medium text-white
            ${
              isOffline
                ? 'bg-red-500'
                : justCameOnline
                ? 'bg-green-500'
                : 'bg-gray-500'
            }
          `}
        >
          <div className="flex items-center justify-center gap-2">
            {isOffline ? (
              <WifiOff className="h-4 w-4" />
            ) : (
              <Wifi className="h-4 w-4" />
            )}
            <span>
              {isOffline
                ? 'Tidak ada koneksi internet'
                : justCameOnline
                ? 'Koneksi internet pulih'
                : 'Online'}
            </span>
            {isOffline && (
              <button
                onClick={handleRetry}
                disabled={isRetrying}
                className="ml-2 rounded bg-white/20 px-2 py-1 text-xs hover:bg-white/30 disabled:opacity-50"
              >
                {isRetrying ? (
                  <RefreshCw className="h-3 w-3 animate-spin" />
                ) : (
                  'Coba Lagi'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Detailed variant - more information
  if (variant === 'detailed') {
    return (
      <div className={`${baseClasses} px-4 py-3`}>
        <div
          className={`
            mx-auto max-w-4xl rounded-lg px-6 py-4 shadow-lg
            ${
              isOffline
                ? 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
                : justCameOnline
                ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
                : 'bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
            }
          `}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`
                  flex h-10 w-10 items-center justify-center rounded-full
                  ${
                    isOffline
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                      : justCameOnline
                      ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                  }
                `}
              >
                {isOffline ? (
                  <WifiOff className="h-5 w-5" />
                ) : justCameOnline ? (
                  <Wifi className="h-5 w-5" />
                ) : (
                  <AlertTriangle className="h-5 w-5" />
                )}
              </div>

              <div>
                <h4
                  className={`
                    font-semibold
                    ${
                      isOffline
                        ? 'text-red-800 dark:text-red-200'
                        : justCameOnline
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-blue-800 dark:text-blue-200'
                    }
                  `}
                >
                  {isOffline
                    ? 'Koneksi Terputus'
                    : justCameOnline
                    ? 'Koneksi Pulih'
                    : 'Status Koneksi'}
                </h4>
                <p
                  className={`
                    text-sm
                    ${
                      isOffline
                        ? 'text-red-600 dark:text-red-300'
                        : justCameOnline
                        ? 'text-green-600 dark:text-green-300'
                        : 'text-blue-600 dark:text-blue-300'
                    }
                  `}
                >
                  {isOffline
                    ? 'Beberapa fitur mungkin tidak tersedia'
                    : justCameOnline
                    ? 'Semua fitur kembali normal'
                    : 'Koneksi stabil'}
                  {lastOnlineTime && isOffline && (
                    <span className="ml-2 text-xs opacity-75">
                      • Terakhir online {getTimeAgo(lastOnlineTime)}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isOffline && (
                <button
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-red-700 dark:hover:bg-red-600"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`}
                  />
                  {isRetrying ? 'Mencoba...' : 'Coba Lagi'}
                </button>
              )}

              <button
                onClick={() => setIsVisible(false)}
                className={`
                  rounded-lg px-3 py-2 text-sm font-medium transition-colors
                  ${
                    isOffline
                      ? 'text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30'
                      : justCameOnline
                      ? 'text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30'
                      : 'text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30'
                  }
                `}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Toast variant - floating notification
  if (variant === 'toast') {
    return (
      <div className={`${baseClasses} pointer-events-none px-4 py-3`}>
        <div className="flex justify-center">
          <div
            className={`
              pointer-events-auto max-w-md rounded-lg px-6 py-4 shadow-xl
              transform transition-all duration-300 ease-out
              ${
                isOffline
                  ? 'bg-red-500 text-white'
                  : justCameOnline
                  ? 'bg-green-500 text-white'
                  : 'bg-blue-500 text-white'
              }
            `}
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {isOffline ? (
                  <WifiOff className="h-5 w-5" />
                ) : (
                  <Wifi className="h-5 w-5" />
                )}
              </div>

              <div className="flex-1">
                <p className="font-medium">
                  {isOffline
                    ? 'Koneksi Terputus'
                    : justCameOnline
                    ? 'Koneksi Pulih'
                    : 'Online'}
                </p>
                <p className="text-sm opacity-90">
                  {isOffline
                    ? 'Mode offline aktif'
                    : justCameOnline
                    ? 'Kembali online'
                    : 'Koneksi stabil'}
                </p>
              </div>

              {isOffline ? (
                <button
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className="flex-shrink-0 rounded bg-white/20 p-2 hover:bg-white/30 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`}
                  />
                </button>
              ) : (
                <button
                  onClick={() => setIsVisible(false)}
                  className="flex-shrink-0 rounded bg-white/20 p-2 hover:bg-white/30"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OfflineIndicator;
